/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import * as Ron from 'blb-ui-types/ron';
import { Box, Button, Text } from 'grommet';
import { downloadNotaryCredentialFile, getUserCredentialDetails } from 'blb-api/ron';
import { ReviewMessageList } from 'blb-components/AuthenticatedApp/RON/NotaryPage/ReviewMessageList';
import { AlertBar, LoadingSpinner } from '@blend/enterprise-components';
import * as Sentry from '@sentry/browser';
import { ReviewConfirmationModal } from 'blb-components/AuthenticatedApp/RON/NotaryPage/ReviewConfirmationModal';
import { CredentialReviewType, UserCredentialType } from 'blb-constants/ron';
import { CredentialAttributes } from 'blb-components/AuthenticatedApp/RON/NotaryPage/CredentialAttributes';
import { CredentialViewer } from 'blb-components/AuthenticatedApp/RON/CredentialViewer/CredentialViewer';
import { DigitalCertificateMeta } from 'blb-ui-types/ronMeta';

export namespace ReviewCredentialModal {
  export interface Props {
    credential: Ron.API.UserCredential;
    principalId: string;
  }
}

export const ReviewCredentialModal: React.FC<ReviewCredentialModal.Props> = ({ principalId, credential }) => {
  const [file, setFile] = useState<File>();
  const [credentialDetails, setCredentialDetails] = useState<Ron.API.UserCredentialDetails>();
  const [reviewConfirmationModalOpen, setReviewConfirmationModalOpen] = useState<boolean>(false);
  const [reviewType, setReviewType] = useState<CredentialReviewType>();
  const [error, setError] = useState<string>();
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  const fetchCredentialDetails = async () => {
    try {
      setCredentialDetails(await getUserCredentialDetails(principalId, credential.id));
    } catch (e) {
      Sentry.captureException(e);
      setError('Failed to fetch credential details');
    }
  };

  const fetchCredentialFile = async () => {
    try {
      if (credential.type === UserCredentialType.DIGITAL_CERTIFICATE) {
        setFile(undefined);
      } else {
        setIsLoadingFile(true);
        const blob = await downloadNotaryCredentialFile(principalId, credential.id);
        setFile(new File([blob], credential?.fileName || 'file.pdf'));
      }
    } catch (e) {
      Sentry.captureException(e);
      setError('Failed to fetch credential file');
    } finally {
      setIsLoadingFile(false);
    }
  };

  useEffect(() => {
    fetchCredentialFile();
    fetchCredentialDetails();
  }, [credential.id]);

  const renderLoading = () => (
    <Box direction="row" justify="center" pad="xlarge">
      <LoadingSpinner size="lg" theme="secondary" />
    </Box>
  );

  const showReviewConfirmationModal = (type: CredentialReviewType) => {
    setReviewConfirmationModalOpen(true);
    setReviewType(type);
  };

  const renderCredentialDetails = (credentialDetails: Ron.API.UserCredentialDetails) => {
    return (
      <>
        <Box border={{ color: 'cloud70', side: 'bottom', size: 'xsmall' }} flex={false} pad="medium">
          <Box>
            <Text size="18px" weight="bold">
              Review Notary Certificate
            </Text>
          </Box>
          <Box margin={{ top: 'xsmall' }}>
            <Text size="small">Review uploaded document and additional data provided</Text>
          </Box>
        </Box>
        <Box flex="grow" pad="medium">
          {credentialDetails ? <CredentialAttributes credential={credentialDetails} /> : renderLoading()}
        </Box>
        <Box
          border={{ color: 'cloud70', side: 'top', size: 'xsmall' }}
          direction="column"
          gap="small"
          justify="end"
          pad={{ horizontal: 'medium', vertical: 'small' }}
        >
          <Box overflow="auto">
            <ReviewMessageList reviews={credentialDetails.reviews} />
          </Box>
          <Box direction="row" flex={false} gap="xsmall" justify="end">
            <Button label="Reject" onClick={() => showReviewConfirmationModal(CredentialReviewType.REJECT)} secondary />
            <Button label="Approve" onClick={() => showReviewConfirmationModal(CredentialReviewType.APPROVE)} primary />
          </Box>
        </Box>
      </>
    );
  };

  const onCloseReviewConfirmationModal = () => {
    setReviewConfirmationModalOpen(false);
    fetchCredentialDetails();
  };
  if (error) {
    return <AlertBar id="it-reviewCredentialModal-alertBar`" message={error} theme="error" />;
  }

  const certificateMetaData: DigitalCertificateMeta | undefined =
    credential.type === UserCredentialType.DIGITAL_CERTIFICATE
      ? (credential.meta as DigitalCertificateMeta)
      : undefined;
  return (
    <Box align="stretch" alignSelf="stretch" background="backgroundGrey" direction="row" flex="grow" height="100%">
      {reviewType && (
        <ReviewConfirmationModal
          credentialId={credential.id}
          handleClose={onCloseReviewConfirmationModal}
          isOpen={reviewConfirmationModalOpen}
          principalId={principalId}
          reviewType={reviewType}
        />
      )}
      <Box elevation="small" flex={{ grow: 1, shrink: 3 }} overflow={{ vertical: 'scroll' }} round="xxsmall">
        <CredentialViewer
          certificateMetaData={certificateMetaData}
          credentialType={credential.type}
          file={file}
          isLoading={isLoadingFile}
        />
      </Box>
      <Box
        background="white"
        basis="1/3"
        direction="column"
        elevation="small"
        flex={{ grow: 0, shrink: 1 }}
        justify="stretch"
        margin="medium"
        round="xxsmall"
      >
        {credentialDetails ? renderCredentialDetails(credentialDetails) : renderLoading()}
      </Box>
    </Box>
  );
};
